import { Button, Grid, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { UseFormMethods, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IPortinForm } from "@features/PortIn/interface/IPortinForm";
import { Loader } from "@common/Loader/Loader";
import { portInPhoneRegex } from "@utils/regex";

interface IParams {
    errorMessage?: string;
    hasTooManyAttempt: boolean;
    phoneNumberSelected: string;
    isGettingCode: boolean;
    isLoading: boolean;
    isSuccess: boolean;
    formMethods: UseFormMethods<IPortinForm>;
    previousPhoneNumber?: string;
    variantStyleButton: "outlined" | "contained";
    changeValue(newPhoneNumber: string): void;
    getCode(): void;
}

export const PhoneNumberView = (props: IParams) => {
    const {
        isSuccess,
        isGettingCode,
        formMethods,
        variantStyleButton,
        changeValue,
        previousPhoneNumber,
        isLoading,
        hasTooManyAttempt,
        errorMessage,
        getCode,
        phoneNumberSelected,
    } = props;

    const { errors, register } = formMethods;

    const { t } = useTranslation(["portin", "validation"]);
    const [tooltip, setTooltip] = useState("");

    const phoneNumber = useWatch<string>({ name: "phoneNumber", control: formMethods.control, defaultValue: "" });
    const getCodeButtonName =
        phoneNumber && phoneNumber === previousPhoneNumber ? t("portin:confirmNumber") : t("portin:getCode");

    useMemo(() => {
        if (phoneNumber.length === 10 && isGettingCode && !isSuccess) {
            const tooltipMessage =
                phoneNumber === previousPhoneNumber
                    ? t("portin:confirmNumberTooltip")
                    : t("portin:validationCodeTooltip");

            setTooltip(tooltipMessage);
        }
    }, [isGettingCode, isSuccess, phoneNumber, previousPhoneNumber, t]);

    const error =
        errors.phoneNumber?.message ??
        (hasTooManyAttempt ? t("portin:tooManyAttempts") : undefined) ??
        errorMessage ??
        tooltip;

    return (
        <Grid alignItems="center" container spacing={2}>
            <Grid item md={6} xs={12}>
                {isSuccess && (
                    <TextField
                        disabled
                        fullWidth
                        id="phoneNumber"
                        label={`${t("portin:phoneNumber")} *`}
                        margin="dense"
                        name="phoneNumber"
                        type="text"
                        value={phoneNumberSelected}
                        variant="standard"
                    />
                )}
                {!isSuccess && (
                    <TextField
                        error={
                            Boolean(errors.phoneNumber) ||
                            hasTooManyAttempt ||
                            Boolean(errorMessage) ||
                            Boolean(tooltip)
                        }
                        fullWidth
                        helperText={error}
                        id="phoneNumber"
                        inputRef={register({
                            required: t("validation:required"),
                            pattern: { value: portInPhoneRegex, message: t("portin:phoneNumberError") },
                        })}
                        label={`${t("portin:phoneNumber")} *`}
                        margin="dense"
                        name="phoneNumber"
                        placeholder="08X XXX XXXX"
                        type="text"
                        variant="standard"
                        onChange={(event) => changeValue(event.target.value)}
                    />
                )}
            </Grid>
            {isGettingCode && (
                <Grid item md={6} sm={3} xs={6}>
                    {isLoading && <Loader size={40} />}
                    {!isLoading && (
                        <Button
                            color="primary"
                            fullWidth
                            id="getCodeButton"
                            variant={variantStyleButton}
                            onClick={getCode}
                        >
                            {getCodeButtonName}
                        </Button>
                    )}
                </Grid>
            )}
        </Grid>
    );
};
